import { formatCurrency } from "../../lib/formatters.js";
import {
  budgetUsagePercent,
  summarizeBudgetRows,
  summarizeMonthlyExpenseRows
} from "../../lib/finance/budgetMath.js";
import "./AnalysisOverview.css";
import { InfoTable } from "../../components/InfoTable/InfoTable.jsx";
import { IconCheck, IconTriangleAlert } from "../../components/Icon/Icon.jsx";
import { ThresholdMeter } from "../../components/ThresholdMeter/ThresholdMeter.jsx";
import { Panel } from "../../components/Panel/Panel.jsx";
import { AnalysisOverviewMeterCard } from "./AnalysisOverviewMeterCard.jsx";
import {
  budgetUsageMeterRange,
  formatBudgetUsagePercentLabel
} from "../../lib/finance/budgetUsageMeter.js";

function statusMarker(tone) {
  return {
    tone,
    content: tone === "good"
      ? <IconCheck />
      : <IconTriangleAlert />
  };
}

function budgetStatus(value, budget) {
  return value <= budget
    ? statusMarker("good")
    : statusMarker("bad");
}

function monthRow(label, value, isCurrentYear, status = null) {
  if (!isCurrentYear) {
    return { label: "\u00A0", value: "\u00A0" };
  }

  return {
    label,
    value,
    status
  };
}

function SummaryColumn({ title, rows, meter }) {
  return (
    <div class="analysis-overview-column">
      <Panel header={title} padded scroll>
        <InfoTable
          rows={rows}
          labelWidth="8.5rem"
          stretch
          frameless
        />
      </Panel>
      <AnalysisOverviewMeterCard {...meter} />
    </div>
  );
}

export function AnalysisOverview({ rows = [], expenseRows = [], dateContext, t = (key) => key }) {
  const summary = summarizeBudgetRows(rows, expenseRows, dateContext);
  const monthlyExpenseSummary = summarizeMonthlyExpenseRows(expenseRows, dateContext);
  const totalPercent = budgetUsagePercent(summary.total.projected, summary.total.budget);
  const ongoingPercent = budgetUsagePercent(summary.ongoing.implied, summary.ongoing.budget);
  const irregularPercent = budgetUsagePercent(summary.irregular.spent, summary.irregular.budget);
  const ongoingCurrentPercent = budgetUsagePercent(
    monthlyExpenseSummary.ongoing.spentThisMonth,
    summary.ongoing.budget
  );
  const totalCurrentPercent = budgetUsagePercent(summary.total.spent, summary.total.budget);
  const summaryColumns = [
    {
      key: "ongoing",
      title: t("ongoing"),
      rows: [
        { label: t("yearlyBudget"), value: formatCurrency(summary.ongoing.budget * 12) },
        { label: t("monthlyBudget"), value: formatCurrency(summary.ongoing.budget) },
        { label: t("dailyBudget"), value: formatCurrency(summary.ongoing.dailyBudget) },
        monthRow(
          t("spentThisMonth"),
          formatCurrency(monthlyExpenseSummary.ongoing.spentThisMonth),
          dateContext.isCurrentYear,
          monthlyExpenseSummary.ongoing.spentThisMonth <= summary.ongoing.budget
            ? statusMarker("good")
            : statusMarker("bad")
        ),
        {
          label: t("spent"),
          value: formatCurrency(summary.ongoing.spent),
          status: summary.ongoing.spent <= summary.ongoing.budget * 12
            ? statusMarker("good")
            : statusMarker("bad")
        },
        {
          label: t("impliedSpend"),
          value: formatCurrency(summary.ongoing.implied),
          status: summary.ongoing.implied <= summary.ongoing.budget
            ? statusMarker("good")
            : statusMarker("bad")
        },
        {
          label: t("monthlyAverage"),
          value: formatCurrency(summary.ongoing.monthlyAverage),
          status: summary.ongoing.monthlyAverage <= summary.ongoing.budget
            ? statusMarker("good")
            : statusMarker("bad")
        }
      ],
      meter: {
        label: t("ongoingSpend"),
        valueLabel: formatBudgetUsagePercentLabel(ongoingPercent),
        levels: [
          {
            key: "implied",
            label: t("implied"),
            percent: ongoingPercent,
            tone: summary.ongoing.implied <= summary.ongoing.budget ? "good" : "bad",
            emphasis: "soft"
          },
          ...(dateContext.isCurrentYear ? [{
            key: "month",
            label: t("actual"),
            percent: ongoingCurrentPercent,
            tone: monthlyExpenseSummary.ongoing.spentThisMonth <= summary.ongoing.budget ? "good" : "bad",
            emphasis: "strong"
          }] : [])
        ]
      }
    },
    {
      key: "irregular",
      title: t("irregular"),
      rows: [
        { label: t("yearlyBudget"), value: formatCurrency(summary.irregular.budget) },
        {
          label: t("spent"),
          value: formatCurrency(summary.irregular.spent),
          status: summary.irregular.spent <= summary.irregular.budget
            ? statusMarker("good")
            : statusMarker("bad")
        },
        {
          label: t("remaining"),
          value: formatCurrency(summary.irregular.remaining),
          status: summary.irregular.remaining !== 0
            ? statusMarker("good")
            : statusMarker("bad")
        },
        monthRow(
          t("spentThisMonth"),
          formatCurrency(monthlyExpenseSummary.irregular.spentThisMonth),
          dateContext.isCurrentYear
        ),
      ],
      meter: {
        label: t("irregularSpend"),
        valueLabel: formatBudgetUsagePercentLabel(irregularPercent),
        levels: [
          {
            key: "spent",
            label: t("spent"),
            percent: irregularPercent,
            tone: summary.irregular.spent <= summary.irregular.budget ? "good" : "bad"
          }
        ]
      }
    },
    {
      key: "total",
      title: t("total"),
      rows: [
        { label: t("yearlyBudget"), value: formatCurrency(summary.total.budget) },
        {
          label: t("spent"),
          value: formatCurrency(summary.total.spent),
          status: budgetStatus(summary.total.spent, summary.total.budget)
        },
        {
          label: t("monthlySpend"),
          value: formatCurrency(summary.total.monthlySpend),
          status: dateContext.isCurrentYear
            ? budgetStatus(summary.total.monthlySpend, summary.total.remainingMonthly)
            : null
        },
        monthRow(
          t("spentThisMonth"),
          formatCurrency(monthlyExpenseSummary.total.spentThisMonth),
          dateContext.isCurrentYear
        ),
        {
          label: t("projectedSpend"),
          value: formatCurrency(summary.total.projected),
          status: budgetStatus(summary.total.projected, summary.total.budget)
        },
        {
          label: t("remaining"),
          value: formatCurrency(summary.total.remaining),
          status: summary.total.remaining > 0
            ? statusMarker("good")
            : statusMarker("bad")
        },
        dateContext.isCurrentYear
          ? { label: t("remainingMonthly"), value: formatCurrency(summary.total.remainingMonthly) }
          : { label: "\u00A0", value: "\u00A0" }
      ],
      meter: {
        label: t("totalSpend"),
        valueLabel: formatBudgetUsagePercentLabel(totalCurrentPercent),
        levels: [
          {
            key: "projected",
            label: t("projected"),
            percent: totalPercent,
            tone: summary.total.projected <= summary.total.budget ? "good" : "bad",
            emphasis: "soft"
          },
          {
            key: "total",
            label: t("spent"),
            percent: totalCurrentPercent,
            tone: summary.total.spent <= summary.total.budget ? "good" : "bad",
            emphasis: "strong"
          }
        ]
      }
    }
  ];
  const topMeters = [
    {
      key: "ongoing",
      label: t("ongoing"),
      value: ongoingPercent,
      valueLabel: formatBudgetUsagePercentLabel(ongoingPercent),
      range: budgetUsageMeterRange
    },
    {
      key: "total",
      label: t("total"),
      value: totalPercent,
      valueLabel: formatBudgetUsagePercentLabel(totalPercent),
      range: budgetUsageMeterRange
    }
  ];

  return (
    <div class="analysis-overview-layout">
      <div class="analysis-overview-top">
        {topMeters.map((meter) => (
          <ThresholdMeter
            key={meter.key}
            label={meter.label}
            value={meter.value}
            valueLabel={meter.valueLabel}
            range={meter.range}
            startValue={meter.range.target}
          />
        ))}
      </div>
      <div class="analysis-overview-columns">
        {summaryColumns.map((column) => (
          <SummaryColumn
            key={column.key}
            title={column.title}
            rows={column.rows}
            meter={column.meter}
          />
        ))}
      </div>
    </div>
  );
}
